// src/store/clientStore.ts
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { Client } from "@/types";
import { clientsApi } from "@/api/endpoints/clients";
import toast from "react-hot-toast";

interface ClientState {
  clients: Client[];
  loading: boolean;
  error: string | null;
  pagination: {
    total: number;
    pages: number;
    page: number;
    limit: number;
  };

  // Actions
  fetchClients: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }) => Promise<void>;
  createClient: (client: Partial<Client>) => Promise<void>;
  updateClient: (id: string, updates: Partial<Client>) => Promise<void>;
  deleteClient: (id: string) => Promise<void>;
  importClients: (file: File) => Promise<void>;
  exportClients: (format: "csv" | "excel") => Promise<void>;
}

export const useClientStore = create<ClientState>()(
  devtools(
    (set, get) => ({
      clients: [],
      loading: false,
      error: null,
      pagination: {
        total: 0,
        pages: 0,
        page: 1,
        limit: 20,
      },

      fetchClients: async (params) => {
        set({ loading: true, error: null });
        try {
          const response = await clientsApi.getClients(params);
          // console.log("Raw response:", response);
          // console.log("Response data:", response.data);

          const responseData = response?.data;
          const clients = responseData?.data?.clients || [];
          const pagination = responseData?.data?.pagination || {
            total: 0,
            pages: 0,
            page: 1,
            limit: 20,
          };

          set({
            clients: clients,
            pagination: pagination,
            loading: false,
          });
        } catch (error: any) {
          console.error("Error fetching clients:", error);
          set({
            error: error.response?.data?.message || error.message,
            loading: false,
          });
          toast.error("Failed to fetch clients");
        }
      },

     createClient: async (client) => {
  try {
    const response = await clientsApi.createClient(client);
    console.log('Create client response:', response.data);
    
    // The response might have the same nested structure
    const newClient = response.data?.data?.client || response.data?.client || response.data;
    
    set((state) => ({
      clients: [...state.clients, newClient],
      pagination: {
        ...state.pagination,
        total: state.pagination.total + 1
      }
    }));
    toast.success('Client created successfully');
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Failed to create client';
    const validationErrors = error.response?.data?.errors;
    
    if (validationErrors && Array.isArray(validationErrors)) {
      validationErrors.forEach((err: any) => {
        toast.error(err.msg || err.message || err);
      });
    } else {
      toast.error(errorMessage);
    }
    throw error;
  }
},

      updateClient: async (id, updates) => {
        try {
          const response = await clientsApi.updateClient(id, updates);
          const updatedClient =
            response.data.data?.client || response.data.client;

          set((state) => ({
            clients: state.clients.map((client) =>
              (client._id || client.id) === id ? updatedClient : client
            ),
          }));
          toast.success("Client updated successfully");
        } catch (error: any) {
          toast.error(
            error.response?.data?.message || "Failed to update client"
          );
          throw error;
        }
      },

      deleteClient: async (id) => {
        try {
          await clientsApi.deleteClient(id);
          set((state) => ({
            clients: state.clients.filter(
              (client) => (client._id || client.id) !== id
            ),
            pagination: {
              ...state.pagination,
              total: Math.max(0, state.pagination.total - 1),
            },
          }));
          toast.success("Client deleted successfully");
        } catch (error: any) {
          toast.error(
            error.response?.data?.message || "Failed to delete client"
          );
        }
      },

      importClients: async (file) => {
        try {
          const response = await clientsApi.importClients(file);
          const result = response.data.data || response.data;

          await get().fetchClients(); // Refresh the list
          toast.success(`${result.imported} clients imported successfully`);

          if (result.failed > 0) {
            toast.error(`${result.failed} clients failed to import`);
          }
        } catch (error: any) {
          toast.error(
            error.response?.data?.message || "Failed to import clients"
          );
        }
      },

      exportClients: async (format) => {
        try {
          const response = await clientsApi.exportClients(format);
          const blob = new Blob([response.data], {
            type:
              format === "csv"
                ? "text/csv"
                : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `clients-${Date.now()}.${
            format === "csv" ? "csv" : "xlsx"
          }`;
          link.click();
          URL.revokeObjectURL(url);
          toast.success("Clients exported successfully");
        } catch (error: any) {
          toast.error(
            error.response?.data?.message || "Failed to export clients"
          );
        }
      },
    }),
    {
      name: "client-store",
    }
  )
);
